/**
 * Farm Service
 * Handles multi-farm operations for users who belong to multiple farms
 */

import { db } from "../firebase";
import {
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  updateDoc,
  query,
  orderBy,
  serverTimestamp,
} from "firebase/firestore";

/**
 * Gets all farms a user belongs to
 *
 * @param {string} userId - The user's ID
 * @returns {Promise<Array>} List of farms the user belongs to
 */
export async function getUserFarms(userId) {
  try {
    const farmsRef = collection(db, "UserFarms", userId, "Farms");
    const q = query(farmsRef, orderBy("lastAccessedAt", "desc"));

    const snapshot = await getDocs(q);
    const farms = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      joinedAt: doc.data().joinedAt?.toDate?.() || null,
      lastAccessedAt: doc.data().lastAccessedAt?.toDate?.() || null,
    }));

    return farms;
  } catch (error) {
    console.error("Error getting user farms:", error);
    throw new Error("Failed to load farms");
  }
}

/**
 * Gets details for a specific farm from the user's perspective
 *
 * @param {string} userId - The user's ID
 * @param {string} farmId - The farm ID
 * @returns {Promise<Object|null>} Farm details or null if not found
 */
export async function getUserFarmDetails(userId, farmId) {
  try {
    const farmRef = doc(db, "UserFarms", userId, "Farms", farmId);
    const farmDoc = await getDoc(farmRef);

    if (!farmDoc.exists()) {
      return null;
    }

    return {
      id: farmDoc.id,
      ...farmDoc.data(),
      joinedAt: farmDoc.data().joinedAt?.toDate?.() || null,
      lastAccessedAt: farmDoc.data().lastAccessedAt?.toDate?.() || null,
    };
  } catch (error) {
    console.error("Error getting farm details:", error);
    throw new Error("Failed to load farm details");
  }
}

/**
 * Sets the active farm for a user
 *
 * @param {string} userId - The user's ID
 * @param {string} farmId - The farm ID to set as active (null for own farm)
 * @returns {Promise<void>}
 */
export async function setActiveFarm(userId, farmId) {
  try {
    // Update the user's activeFarmId
    const userRef = doc(db, "Users", userId);
    await updateDoc(userRef, {
      activeFarmId: farmId,
    });

    // Update lastAccessedAt for the farm
    if (farmId) {
      const farmRef = doc(db, "UserFarms", userId, "Farms", farmId);
      await updateDoc(farmRef, {
        lastAccessedAt: serverTimestamp(),
      });
    }
  } catch (error) {
    console.error("Error setting active farm:", error);
    throw new Error("Failed to switch farm");
  }
}

/**
 * Gets the active farm ID for a user
 *
 * @param {string} userId - The user's ID
 * @returns {Promise<string|null>} The active farm ID or null if using own farm
 */
export async function getActiveFarmId(userId) {
  try {
    const userRef = doc(db, "Users", userId);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {
      return null;
    }

    return userDoc.data().activeFarmId || null;
  } catch (error) {
    console.error("Error getting active farm ID:", error);
    return null;
  }
}

/**
 * Initializes the user's own farm in UserFarms collection
 * Called when user creates invitations for the first time
 *
 * @param {string} userId - The user's ID
 * @param {string} farmName - The farm name
 * @returns {Promise<void>}
 */
export async function initializeOwnFarm(userId, farmName) {
  try {
    const farmRef = doc(db, "UserFarms", userId, "Farms", userId);
    const farmDoc = await getDoc(farmRef);

    // Only initialize if not already exists
    if (!farmDoc.exists()) {
      await setDoc(farmRef, {
        farmId: userId,
        farmName: farmName || "My Farm",
        role: 1, // Owner is always Admin
        isOwner: true,
        joinedAt: serverTimestamp(),
        lastAccessedAt: serverTimestamp(),
        isPrimary: true,
      });
    }
  } catch (error) {
    console.error("Error initializing own farm:", error);
    throw new Error("Failed to initialize farm");
  }
}

/**
 * Updates the farm name for a user's own farm
 *
 * @param {string} userId - The user's ID (farm owner)
 * @param {string} farmName - The new farm name
 * @returns {Promise<void>}
 */
export async function updateFarmName(userId, farmName) {
  try {
    // Update in user document
    const userRef = doc(db, "Users", userId);
    await updateDoc(userRef, {
      farmName: farmName,
    });

    // Update in UserFarms for the owner
    const ownFarmRef = doc(db, "UserFarms", userId, "Farms", userId);
    const ownFarmDoc = await getDoc(ownFarmRef);
    if (ownFarmDoc.exists()) {
      await updateDoc(ownFarmRef, {
        farmName: farmName,
      });
    }

    // Note: We should also update farmName in all members' UserFarms entries
    // This could be handled by a Cloud Function for consistency
  } catch (error) {
    console.error("Error updating farm name:", error);
    throw new Error("Failed to update farm name");
  }
}

/**
 * Checks if a user has access to multiple farms
 *
 * @param {string} userId - The user's ID
 * @returns {Promise<boolean>} True if user has access to more than one farm
 */
export async function hasMultipleFarms(userId) {
  try {
    const farms = await getUserFarms(userId);
    return farms.length > 1;
  } catch (error) {
    console.error("Error checking multiple farms:", error);
    return false;
  }
}

/**
 * Gets the user's role on a specific farm
 *
 * @param {string} userId - The user's ID
 * @param {string} farmId - The farm ID
 * @returns {Promise<number|null>} Role number or null if not a member
 */
export async function getUserRoleOnFarm(userId, farmId) {
  try {
    // If it's their own farm, they're the owner (Admin)
    if (userId === farmId) {
      return 1;
    }

    const farmDetails = await getUserFarmDetails(userId, farmId);
    return farmDetails?.role || null;
  } catch (error) {
    console.error("Error getting user role on farm:", error);
    return null;
  }
}

/**
 * Leaves a farm (for team members, not owners)
 *
 * @param {string} userId - The user's ID
 * @param {string} farmId - The farm ID to leave
 * @returns {Promise<Object>} Result with leave status
 */
export async function leaveFarm(userId, farmId) {
  // This is handled by the removeMember Cloud Function
  // Imported here for convenience
  const { removeMember } = await import("./teamService");
  return removeMember(farmId, userId);
}
